import type { ReactNode } from "react";
import { renderInlineMarkdown } from "../lib/markdown";

export type ReviewItem = {
  id: string;
  label: string;
  choice: "Packed" | "Left behind";
  verdict: string;
  tone: "good" | "risk" | "warn";
  explanation: string;
};

/**
 * Teaching "learning card" shown after a device beat. Two framings share the
 * same body/spot/protect/remember fields + layout:
 *  - "risky" (default): an incident debrief — ATTACK (what's going on) ->
 *    HOW TO SPOT (red flags) -> HOW TO PROTECT (prevention) -> REMEMBER.
 *  - "safe": praise for the correct choice PLUS the same risk education.
 *  - "recap": end-of-course summary — TODAY / WATCH FOR / DO THIS / REMEMBER —
 *    used when there was no just-made choice to praise (e.g. after a game).
 */
export function TeachOverlay({
  body,
  spot,
  protect,
  remember,
  reviewItems,
  outcome = "risky",
  continueLabel,
  altContinue,
  onContinue,
  onAltContinue,
  whatIf,
  whatIfLabel,
  heading,
}: {
  body: string;
  spot: string[];
  protect: string[];
  remember?: string;
  reviewItems?: ReviewItem[];
  outcome?: "risky" | "safe" | "recap" | "mixed";
  continueLabel?: string;
  altContinue?: { label: string; outcomeKey: string; prominent?: boolean };
  onContinue: () => void;
  onAltContinue?: (outcomeKey: string) => void;
  /** Hypothetical being replayed after a safe choice (store `whatIf`). */
  whatIf?: string | null;
  whatIfLabel?: string;
  /** Optional heading wording; anything unset keeps the `outcome` default. */
  heading?: { eyebrow?: string; title?: string; spotEyebrow?: string; spotTitle?: string; protectEyebrow?: string; protectTitle?: string };
}) {
  const baseCopy =
    outcome === "recap"
      ? {
          attackEyebrow: "TODAY",
          attackTitle: "What you practiced",
          spotEyebrow: "WATCH FOR",
          spotTitle: "What to look for next time",
          protectEyebrow: "DO THIS",
          protectTitle: "How to stay safe",
          firstIcon: "\u{1F4CB}",
          firstTone: "good" as const,
        }
      : outcome === "safe"
      ? {
          attackEyebrow: "GOOD CALL",
          attackTitle: "Why was that the right move?",
          spotEyebrow: "STILL WATCH FOR",
          spotTitle: "What else can still go wrong here",
          protectEyebrow: "KEEP DOING THIS",
          protectTitle: "How to keep making the safe call",
          firstIcon: "\u2705",
          firstTone: "good" as const,
        }
      : outcome === "mixed"
      ? {
          attackEyebrow: "WRONG CATEGORY",
          attackTitle: "The description was right. The category was not.",
          spotEyebrow: "WHY THAT MATTERS",
          spotTitle: "Where this ticket would have gone",
          protectEyebrow: "NEXT TIME",
          protectTitle: "Set the category to the real incident",
          firstIcon: "!",
          firstTone: "spot" as const,
        }
      : {
          attackEyebrow: "ATTACK",
          attackTitle: "What is going on?",
          spotEyebrow: "HOW TO SPOT",
          spotTitle: "What could you have spotted?",
          protectEyebrow: "HOW TO PROTECT",
          protectTitle: "What can you do to prevent this?",
          firstIcon: "\u0021",
          firstTone: "attack" as const,
        };
  const copy = {
    ...baseCopy,
    attackEyebrow: heading?.eyebrow ?? baseCopy.attackEyebrow,
    attackTitle: heading?.title ?? baseCopy.attackTitle,
    spotEyebrow: heading?.spotEyebrow ?? baseCopy.spotEyebrow,
    spotTitle: heading?.spotTitle ?? baseCopy.spotTitle,
    protectEyebrow: heading?.protectEyebrow ?? baseCopy.protectEyebrow,
    protectTitle: heading?.protectTitle ?? baseCopy.protectTitle,
  };
  if (reviewItems && reviewItems.length > 0) {
    const approved = reviewItems.filter((item) => item.tone === "good").length;
    return (
      <div className="teach">
        <div className="learn-card pack-review-card">
          <header className="pack-review-head">
            <div>
              <div className="learn-eyebrow protect">YOUR PACKING REVIEW</div>
              <h2>What belongs in Max&apos;s travel kit?</h2>
              <p>
                Here is exactly what Max packed, what he left behind, and the
                security reason behind each decision.
              </p>
            </div>
            <div className="pack-review-score">
              <strong>{approved}/{reviewItems.length}</strong>
              <span>good decisions</span>
            </div>
          </header>

          <div className="pack-review-table" role="table" aria-label="Packing choices review">
            <div className="pack-review-row pack-review-labels" role="row">
              <span role="columnheader">Item</span>
              <span role="columnheader">Max&apos;s choice</span>
              <span role="columnheader">Result</span>
              <span role="columnheader">Why</span>
            </div>
            {reviewItems.map((item) => (
              <div className={`pack-review-row ${item.tone}`} role="row" key={item.id}>
                <strong role="cell">{item.label}</strong>
                <span role="cell" className="pack-review-choice">{item.choice}</span>
                <span role="cell">
                  <span className={`pack-review-verdict ${item.tone}`}>{item.verdict}</span>
                </span>
                <span role="cell" className="pack-review-why">{item.explanation}</span>
              </div>
            ))}
          </div>

          {remember && (
            <div className="learn-remember">
              <span className="learn-ico remember">{"\u{1F4A1}"}</span>
              <div>
                <span className="learn-remember-label">REMEMBER:</span>{" "}
                <span dangerouslySetInnerHTML={{ __html: renderInlineMarkdown(remember) }} />
              </div>
            </div>
          )}

          <div className="learn-actions">
            <button className="btn" onClick={onContinue}>
              Continue to the station
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`teach ${outcome === "safe" ? "tone-safe" : outcome === "recap" ? "tone-recap" : "tone-risky"}`}>
      <div className="learn-card">
        {whatIf && outcome !== "safe" && (
          <div className="cf-whatif cf-whatif--teach">
            <span className="cf-whatif__label">{whatIfLabel ?? "What would have happened"}</span>
            <span className="cf-whatif__text">{whatIf}</span>
          </div>
        )}
        <Section
          tone={copy.firstTone}
          icon={copy.firstIcon}
          eyebrow={copy.attackEyebrow}
          title={copy.attackTitle}
        >
          <p
            className="learn-body"
            dangerouslySetInnerHTML={{ __html: renderInlineMarkdown(body) }}
          />
        </Section>

        {spot.length > 0 && (
          <Section
            tone="spot"
            icon={"\u{1F50D}"}
            eyebrow={copy.spotEyebrow}
            title={copy.spotTitle}
          >
            <ul className="learn-list">
              {spot.map((s, i) => (
                <li key={i} dangerouslySetInnerHTML={{ __html: renderInlineMarkdown(s) }} />
              ))}
            </ul>
          </Section>
        )}

        {protect.length > 0 && (
          <Section
            tone="protect"
            icon={"\u{1F6E1}"}
            eyebrow={copy.protectEyebrow}
            title={copy.protectTitle}
          >
            <ul className="learn-list green">
              {protect.map((s, i) => (
                <li key={i} dangerouslySetInnerHTML={{ __html: renderInlineMarkdown(s) }} />
              ))}
            </ul>
          </Section>
        )}

        {remember && (
          <div className="learn-remember">
            <span className="learn-ico remember">{"\u{1F4A1}"}</span>
            <div>
              <span className="learn-remember-label">REMEMBER:</span>{" "}
              <span dangerouslySetInnerHTML={{ __html: renderInlineMarkdown(remember) }} />
            </div>
          </div>
        )}

        <div className="learn-actions">
          {altContinue?.prominent && (
            <button
              className="btn teach-alt-btn--prominent"
              onClick={() => onAltContinue?.(altContinue.outcomeKey)}
            >
              {altContinue.label}
            </button>
          )}
          <button className="btn" onClick={onContinue}>
            {continueLabel ?? "I understand — continue"}
          </button>
          {altContinue && !altContinue.prominent && (
            <button
              className="btn btn-ghost teach-alt-btn"
              onClick={() => onAltContinue?.(altContinue.outcomeKey)}
            >
              {altContinue.label}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function Section({
  tone,
  icon,
  eyebrow,
  title,
  children,
}: {
  tone: "attack" | "good" | "spot" | "protect";
  icon: string;
  eyebrow: string;
  title: string;
  children: ReactNode;
}) {
  return (
    <div className={`learn-sec tone-${tone}`}>
      <span className={`learn-ico ${tone}`}>{icon}</span>
      <div className="learn-sec-main">
        <div className={`learn-eyebrow ${tone}`}>{eyebrow}</div>
        <h3 className="learn-title">{title}</h3>
        {children}
      </div>
    </div>
  );
}
